import i18n from '../../i18n';
import { formatDate, formatNumber } from '../../i18n/format';

export type MyVoucher = {
    id?: string;
    voucherId?: string;
    orderId?: string;
    brand?: string;
    brandName?: string;
    name?: string;
    nameEn?: string;
    key?: string;
    faceValue?: string | number;
    faceCurrency?: string;
    amount?: number;
    value?: number;
    currency?: string;
    region?: string;
    regionName?: string;
    country?: string;
    deliveredAt?: string;
    date?: string;
    purchasedAt?: string;
    createdAt?: string;
    revealed?: boolean;
    code?: string;
    pin?: string;
    redemptionUrl?: string;
    revealedAt?: string;
    imageUrl?: string;
    logoUrl?: string;
    status?: string;
    product?: {
        name?: string;
        nameEn?: string;
        logoUrl?: string;
        regionName?: string;
        currency?: string;
    };
    denomination?: {
        value?: number;
        currency?: string;
        labelEn?: string;
    };
};

export type VoucherUsageStatus = 'USED' | 'READY';

export function getVoucherId(voucher: MyVoucher) {
    return voucher.voucherId || voucher.id || '';
}

export function getVoucherUsageStatus(voucher: MyVoucher): VoucherUsageStatus {
    return voucher.status?.toUpperCase() === 'USED' ? 'USED' : 'READY';
}

export function getVoucherName(voucher: MyVoucher) {
    return (
        voucher.nameEn ||
        voucher.name ||
        voucher.brandName ||
        voucher.brand ||
        voucher.product?.nameEn ||
        voucher.product?.name ||
        i18n.t('fallback.giftCard', { ns: 'vouchers' })
    );
}

export function getVoucherLogo(voucher: MyVoucher) {
    return voucher.imageUrl || voucher.logoUrl || voucher.product?.logoUrl;
}

export function getVoucherRegion(voucher: MyVoucher) {
    return (
        voucher.regionName ||
        voucher.region ||
        voucher.product?.regionName ||
        voucher.country ||
        ''
    );
}

function formatFaceValue(value: string | number) {
    const numeric = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(numeric)) return String(value);
    return formatNumber(numeric, { maximumFractionDigits: 6 });
}

export function getVoucherAmount(voucher: MyVoucher) {
    const value = voucher.faceValue ?? voucher.denomination?.value ?? voucher.amount ?? voucher.value;
    const currency =
        voucher.faceCurrency ||
        voucher.denomination?.currency ||
        voucher.currency ||
        voucher.product?.currency ||
        '';

    if (value == null || value === '') return '—';
    return `${formatFaceValue(value)} ${currency}`.trim();
}

export function getVoucherCode(voucher: MyVoucher) {
    return voucher.code || voucher.pin || '';
}

export function formatVoucherDate(value?: string) {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return formatDate(parsed, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
}

export function getVoucherDate(voucher: MyVoucher) {
    return formatVoucherDate(
        voucher.deliveredAt || voucher.date || voucher.purchasedAt || voucher.createdAt
    );
}
